import { useNavigate } from 'react-router-dom';
import { Button, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';

import { projectsApiKeyStore } from '../../../../api/projects-api-key-store';
import { SiTrackGrid } from '../../../../components/si-track-grid/si-track-grid';
import { ProjectCustomGridCell } from '../../../projects/project-custom-grid-cell';
import { columns } from './projects-grid-columns';
import { GuardedComponent } from '../../../../components/guarded-component';
import { UserPermissionName } from '../../../../models/user-permission-name';
import { useAuthorize } from '../../../../hooks/use-authorize';
import { useLoadData } from '../../../../hooks/useLoadData';
import { gridApiKeyStore } from '../../../../api/grid-api-key-store';
import { State } from '@progress/kendo-data-query';
import { useEffect, useState } from 'react';
import { GridColumnConfig, GridPreference } from '../../../../models/gridCoumnConfig';
import useDebounce from '../../../../hooks/use-debounce';
import { ApiResponse } from '../../../../models/api-response';
import { Project } from '../../../../models/project';
import { useSaveData } from '../../../../hooks/useSaveData';

interface ProjectsTabProps {
  customerID: number;
  officeID: number;
}

export const ProjectsTab = ({ customerID, officeID }: ProjectsTabProps) => {
  const navigate = useNavigate();

  const redirectCreateProject = () => {
    if (customerID && officeID) navigate(`/projects/create?customerID=${customerID}&officeID=${officeID}`);
  };

  const addWorkOrderElement = (dataItem: any) => {
    const projectID = dataItem?.dataItem?.projectID;

    const redirectAddLot = (projectID: any) => {
      navigate(`/projects/edit/${projectID}?tab=buildings`);
    };
    const isAddUnitPermission = useAuthorize([UserPermissionName.ADD_UNIT]);

    return (
      <>
        <GuardedComponent requiredPermissions={[UserPermissionName.ADD_UNIT]}>
          <IconButton aria-label="view" onClick={() => redirectAddLot(projectID)}>
            <Tooltip title={<Typography variant="body1">{'Add Lot/Unit'}</Typography>}>
              <AddCircleOutlineOutlinedIcon style={{ color: '$primary-color' }} />
            </Tooltip>
          </IconButton>
        </GuardedComponent>
        {!isAddUnitPermission && (
          <Tooltip title={<Typography variant="body1">{'you do not have add Lot/Unit permission'}</Typography>}>
            <IconButton aria-label="view">
              <AddCircleOutlineOutlinedIcon style={{ color: 'gray' }} />
            </IconButton>
          </Tooltip>
        )}
      </>
    );
  };

  // -------------------------------------------------Grid Layout------------------------------

  const {
    isLoading: isLoadingGridLayout,
    data: gridLayouts,
    refetch: refetchGridLayout,
  } = useLoadData<ApiResponse<GridPreference>>(gridApiKeyStore.getGridLayout('Office Details', 'Project'));

  const {
    isLoading,
    data: Projects,
    refetch,
  } = useLoadData<ApiResponse<Project[]>>(projectsApiKeyStore.getOfficeProjects(officeID));

  const { mutateAsync } = useSaveData<any>(gridApiKeyStore.saveGridLayOut());
  const [gridConfig, setGridConfig] = useState<{
    dataState: State | undefined;
    columns: any[] | undefined;
  }>();

  const [filteredProjects, setFilteredProjects] = useState<Project[]>([]);

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  useEffect(() => {
    const filteredProjectList = Projects?.data?.filter((Project) =>
      Object.values(Project).some(
        (value) => value && value.toString().toLowerCase().includes(debouncedSearchTerm.toLowerCase())
      )
    );
    setFilteredProjects(filteredProjectList || []);
  }, [debouncedSearchTerm, Projects?.data]);

  const handleInputChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleSaveLayout = (gridDataState: State, gridColumns: GridColumnConfig[]) => {
    const data = {
      filterJSON: JSON.stringify(gridDataState),
      columnJSON: JSON.stringify(gridColumns),
      gridName: 'Project',
      pageName: 'Office Details',
    };

    mutateAsync({ data }).then(() => {
      refetchGridLayout();
    });
  };

  const handleResetLayout = () => {
    const data = {
      filterJSON: null,
      columnJSON: null,
      gridName: 'Project',
      pageName: 'Office Details',
    };

    mutateAsync({ data }).then(() => {
      refetchGridLayout();
    });
  };

  useEffect(() => {
    if (!gridLayouts?.data) return;

    try {
      const filterData = gridLayouts.data.filterJSON ? JSON.parse(gridLayouts.data.filterJSON) : null;

      const columnJson = gridLayouts.data.columnJSON ? JSON.parse(gridLayouts.data.columnJSON) : null;

      // if (filterData && columnJson) {
      setGridConfig({
        columns: columnJson,
        dataState: filterData,
      });
      // }
    } catch (error) {
      console.log('Error parsing grid layout JSON:', error);
    }
  }, [gridLayouts?.data]);

  // ----------------------------------------------------------------------------------------

  return (
    <div className="borderTop">
      <GuardedComponent requiredPermissions={[UserPermissionName.ADD_PROJECT]}>
        <Button
          className="innerTab-button"
          size="small"
          variant="contained"
          disableElevation={true}
          startIcon={<AddIcon />}
          onClick={redirectCreateProject}
        >
          Add Project
        </Button>
      </GuardedComponent>
      <SiTrackGrid
        fileTitle='office_project'
        columns={columns}
        dataItemKey="projectID"
        customCell={(field) =>
          ProjectCustomGridCell({
            field,
          })
        }
        // queryConfig={projectsApiKeyStore.getOfficeProjects(officeID)}
        viewRoute={(value) => `/projects/details/${value.projectID}`}
        CustomComponent={addWorkOrderElement}
        actionPermissions={{
          view: UserPermissionName.VIEW_PROJECT,
          export: UserPermissionName.DOWNLOAD_GRID,
        }}
        // -----------------Grid Layout-------
        dataItems={filteredProjects || []}
        reorderable
        handleSearch={handleInputChange}
        handleSaveLayout={handleSaveLayout}
        isSaveLayout={true}
        gridConfigFromApi={gridConfig}
        handleResetLayout={handleResetLayout}
      // -----------------Grid Layout-------
      />
    </div>
  );
};
